<template>
  <AppModal
    :open="open"
    :title="modalTitle"
    :description="modalTitle"
    test-id="add-reservation-modal"
    @close="emit('close')"
  >
    <form
      novalidate
      class="min-w-0 space-y-4"
      @submit.prevent="onSubmit"
    >
      <p v-if="formError" class="text-sm text-red-600 dark:text-red-400" role="alert">{{ formError }}</p>

      <div>
        <label for="add-res-booking-date" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {{ $t('app.reservationsTableDate') }} <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="add-res-booking-date"
          v-model="form.booking_date"
          type="date"
          class="w-full min-h-[44px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-zinc-800 px-3 py-2 text-charcoal dark:text-white"
          :min="todayYmd"
          :aria-invalid="!!fieldErrors.booking_date"
          :aria-describedby="fieldErrors.booking_date ? 'add-res-booking-date-err' : undefined"
        >
        <p id="add-res-booking-date-err" v-if="fieldErrors.booking_date" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.booking_date }}</p>
      </div>

      <div>
        <label for="add-res-booking-time" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {{ $t('app.reservationsTableTime') }} <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="add-res-booking-time"
          v-model="form.booking_time"
          type="time"
          class="w-full min-h-[44px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-zinc-800 px-3 py-2 text-charcoal dark:text-white"
          :aria-invalid="!!fieldErrors.booking_time"
          :aria-describedby="fieldErrors.booking_time ? 'add-res-booking-time-err' : undefined"
        >
        <p id="add-res-booking-time-err" v-if="fieldErrors.booking_time" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.booking_time }}</p>
      </div>

      <div>
        <label for="add-res-pax" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {{ $t('app.reservationsTablePax') }} <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="add-res-pax"
          v-model.number="form.pax"
          type="number"
          min="1"
          max="99"
          class="w-full min-h-[44px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-zinc-800 px-3 py-2 text-charcoal dark:text-white"
          :aria-invalid="!!fieldErrors.pax"
          :aria-describedby="fieldErrors.pax ? 'add-res-pax-err' : undefined"
        >
        <p id="add-res-pax-err" v-if="fieldErrors.pax" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.pax }}</p>
      </div>

      <div>
        <label for="add-res-customer-name" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {{ $t('app.reservationsTableCustomer') }} <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="add-res-customer-name"
          v-model.trim="form.customer_name"
          type="text"
          autocomplete="name"
          maxlength="255"
          class="w-full min-h-[44px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-zinc-800 px-3 py-2 text-charcoal dark:text-white"
          :aria-invalid="!!fieldErrors.customer_name"
          :aria-describedby="fieldErrors.customer_name ? 'add-res-customer-name-err' : undefined"
        >
        <p id="add-res-customer-name-err" v-if="fieldErrors.customer_name" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.customer_name }}</p>
      </div>

      <div>
        <label for="add-res-customer-mobile" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {{ $t('app.reservationsTableMobile') }} <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="add-res-customer-mobile"
          v-model.trim="form.customer_mobile"
          type="tel"
          autocomplete="tel"
          class="w-full min-h-[44px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-zinc-800 px-3 py-2 text-charcoal dark:text-white"
          :placeholder="$t('reservation.mobilePlaceholder')"
          :aria-invalid="!!fieldErrors.customer_mobile"
          :aria-describedby="fieldErrors.customer_mobile ? 'add-res-customer-mobile-err' : undefined"
        >
        <p id="add-res-customer-mobile-err" v-if="fieldErrors.customer_mobile" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.customer_mobile }}</p>
      </div>

      <div>
        <label for="add-res-status" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {{ $t('app.reservationsTableStatus') }}
        </label>
        <select
          id="add-res-status"
          v-model="form.status"
          class="w-full min-h-[44px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-zinc-800 px-3 py-2 text-charcoal dark:text-white"
        >
          <option value="pending">{{ $t('app.reservationStatus_pending') }}</option>
          <option value="approved">{{ $t('app.reservationStatus_approved') }}</option>
        </select>
      </div>

      <div class="flex flex-wrap gap-2 pt-2">
        <AppButton type="submit" variant="primary" class="min-h-[44px]" :disabled="submitting">
          {{ submitting ? $t('app.loading') : $t('app.addReservation') }}
        </AppButton>
        <AppButton type="button" variant="secondary" class="min-h-[44px]" :disabled="submitting" @click="emit('close')">
          {{ $t('app.cancel') }}
        </AppButton>
      </div>
    </form>
  </AppModal>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AppModal from '@/components/ui/AppModal.vue'
import AppButton from '@/components/ui/AppButton.vue'
import { reservationService, normalizeApiError, getValidationErrors } from '@/services'
import { formatDisplayDate, todayYmd as getTodayYmd } from '@/utils/date'

const props = defineProps({
  open: { type: Boolean, default: false },
  /** Pre-fill booking date (Y-m-d) when owner clicked a calendar date */
  initialDate: { type: String, default: '' },
  restaurantUuid: { type: String, default: '' },
})

const emit = defineEmits(['close', 'created'])

const { t } = useI18n()

const form = ref({
  booking_date: '',
  booking_time: '19:00',
  customer_name: '',
  customer_mobile: '',
  pax: 2,
  status: 'pending',
})
const fieldErrors = ref({})
const formError = ref('')
const submitting = ref(false)

/** Today YYYY-MM-DD (owners can book today or any future date). */
const todayYmd = computed(() => getTodayYmd())

const modalTitle = computed(() => {
  if (props.initialDate) {
    const formatted = formatDisplayDate(props.initialDate)
    if (formatted) return t('app.addReservationForDate', { date: formatted })
  }
  return t('app.addReservation')
})

watch(
  () => [props.open, props.initialDate],
  () => {
    if (props.open) {
      form.value = {
        booking_date: props.initialDate || todayYmd.value,
        booking_time: '19:00',
        customer_name: '',
        customer_mobile: '',
        pax: 2,
        status: 'pending',
      }
      fieldErrors.value = {}
      formError.value = ''
    }
  },
  { immediate: true }
)

function isValidInternationalPhone(value) {
  if (typeof value !== 'string') return false
  const trimmed = value.trim().replace(/\s/g, '')
  return /^\+?[0-9]{10,15}$/.test(trimmed)
}

function normalizeMobile(value) {
  const s = (value ?? '').trim().replace(/\s/g, '')
  if (/^\+[0-9]{10,15}$/.test(s)) return s
  if (/^[0-9]{10,15}$/.test(s)) return `+${s}`
  return s
}

function validate() {
  const err = {}
  const p = Number(form.value.pax)
  if (Number.isNaN(p) || p < 1 || p > 99) {
    err.pax = t('app.addReservationPaxInvalid')
  }
  const d = (form.value.booking_date ?? '').trim()
  if (!d) {
    err.booking_date = t('reservation.dateRequired')
  } else if (d < todayYmd.value) {
    err.booking_date = t('reservation.dateFuture')
  }
  const tm = (form.value.booking_time ?? '').trim()
  if (!tm) err.booking_time = t('reservation.timeRequired')
  const name = (form.value.customer_name ?? '').trim()
  if (!name) err.customer_name = t('reservation.nameRequired')
  else if (name.length > 255) err.customer_name = t('reservation.nameMax')
  const mobile = (form.value.customer_mobile ?? '').trim()
  if (!mobile) err.customer_mobile = t('reservation.mobileRequired')
  else if (!isValidInternationalPhone(mobile)) err.customer_mobile = t('reservation.mobileInvalidInternational')
  fieldErrors.value = err
  return Object.keys(err).length === 0
}

async function onSubmit() {
  formError.value = ''
  fieldErrors.value = {}
  if (!validate()) return
  if (!props.restaurantUuid) return
  submitting.value = true
  try {
    const payload = {
      booking_date: form.value.booking_date.trim(),
      booking_time: form.value.booking_time.trim(),
      customer_name: form.value.customer_name.trim(),
      customer_mobile: normalizeMobile(form.value.customer_mobile),
      pax: Number(form.value.pax),
      status: form.value.status,
    }
    await reservationService.create(props.restaurantUuid, payload)
    emit('created')
    emit('close')
  } catch (e) {
    const errors = getValidationErrors(e)
    fieldErrors.value = {
      booking_date: errors.booking_date,
      booking_time: errors.booking_time,
      customer_name: errors.customer_name,
      customer_mobile: errors.customer_mobile,
      pax: errors.pax,
    }
    formError.value = Object.values(errors).find(Boolean) ?? normalizeApiError(e).message ?? t('app.failedToUpdate')
  } finally {
    submitting.value = false
  }
}
</script>
